import type { Dated, DateRange } from '@iculogbook/shared';

/**
 * The dashboard's date-range selector, as pure functions.
 *
 * `now` is passed in everywhere, exactly like the aggregations these feed. A
 * range that quietly means "whenever this rendered" cannot be tested and cannot
 * be reproduced tomorrow.
 */

export const RANGE_KEYS = ['30d', '3m', '12m', 'all'] as const;
export type RangeKey = (typeof RANGE_KEYS)[number];

export const RANGE_LABELS: Record<RangeKey, string> = {
  '30d': '30 days',
  '3m': '3 months',
  '12m': '12 months',
  all: 'All',
};

/** The end of the day `now` falls in, so today's entries are always included. */
function endOfDay(now: Date): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

/**
 * `all` runs from the earliest entry a trainee has, not from some arbitrary
 * epoch: the monthly buckets are built from the range, so a range starting in
 * 1990 would draw thirty years of empty months.
 *
 * `entries` is typed structurally rather than as `Entry`, so callers can pass
 * clinical entries and EDT entries together — "all time" that began after a
 * trainee's first teaching session would silently exclude it.
 */
export function rangeFor(key: RangeKey, now: Date, entries: readonly Dated[] = []): DateRange {
  const to = endOfDay(now);

  if (key === 'all') {
    const earliest = entries
      .filter((entry) => entry.deletedAt === undefined)
      .reduce<Date | null>(
        (found, entry) =>
          found === null || entry.occurredAt.getTime() < found.getTime() ? entry.occurredAt : found,
        null,
      );

    return { from: startOfDay(earliest ?? new Date(now.getFullYear(), now.getMonth(), 1)), to };
  }

  const from = new Date(now);
  if (key === '30d') from.setDate(from.getDate() - 29);
  if (key === '3m') from.setMonth(from.getMonth() - 3);
  if (key === '12m') from.setMonth(from.getMonth() - 12);

  return { from: startOfDay(from), to };
}

/** The "last 30 days" headline tile, which is fixed however the selector is set. */
export function last30Days(now: Date): DateRange {
  return rangeFor('30d', now);
}

/**
 * The activity heatmap's window — fixed at 20 trailing weeks however the
 * period selector is set, the same reasoning as `last30Days`: "have I been
 * consistent lately" is a question about real calendar weeks, not about
 * whatever range someone happens to have picked for the figures above it.
 *
 * 20 weeks rather than a full year: this is meant to be glanced at on a phone
 * without scrolling, and the recent past is the part a trainee can still do
 * something about — a year-long graph is mostly a record of days already gone.
 * 20 rather than 16 (its original figure) fills the card's width better —
 * the grid is fixed-size dots, so a shorter window used to leave a stub of
 * empty card beside it once the day-label column and legend had their room.
 */
export function lastWeeks(now: Date, weeks = 20): DateRange {
  const from = new Date(now);
  from.setDate(from.getDate() - (weeks * 7 - 1));
  return { from: startOfDay(from), to: endOfDay(now) };
}
